import { IUnitSchedule, Schedule } from "@auvious/utils";
import {
  Component,
  Prop,
  h,
  Watch,
  EventEmitter,
  State,
  Event,
  ComponentInterface,
} from "@stencil/core";
// import { CalendarEntry } from '../../utils/calendar-entry';
import { Calendar, CalendarEntry } from "../../../models";
import { TranslateService } from "../../../services";

@Component({
  tag: "av-calendar",
  styleUrl: "av-calendar.scss",
  // shadow: true,
})
export class AvCalendar implements ComponentInterface {
  @Prop() dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  @Prop() monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  @Prop() showFillDays = true;
  @Prop() minDate: CalendarEntry;
  @Prop() selectedDate: CalendarEntry;
  @Prop() schedule: Schedule;

  @State() date = Calendar.getToday();
  @State() daysInMonth: number[];
  @State() eventDates = [];

  @Event({
    eventName: "dayChanged",
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  dayChanged: EventEmitter<CalendarEntry>;
  @Event({
    eventName: "monthChanged",
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  monthChanged: EventEmitter<CalendarEntry>;

  private fillStartCount: number;
  private fillEndCount: number;
  readonly today: CalendarEntry;
  private dailySchedule: readonly IUnitSchedule[];

  constructor() {
    this.today = Calendar.getToday();
  }

  @Watch("selectedDate")
  watchDate(date: CalendarEntry): void {
    if ("month" in date && "year" in date) {
      this.date = { ...date };
    }
  }

  componentWillLoad() {
    this.setCalendarDetails();
  }

  setCalendarDetails(): void {
    let date = this.getValidDate();
    const calendar = new Calendar(date.year, date.month);
    this.daysInMonth = calendar.getCalendarDays();

    // make sure a day contains some minimum 15min interval
    this.dailySchedule = this.schedule
      .getDays(date.year, date.month - 1)
      .map((daily, day) => {
        if (
          !this.schedule.nextOpenTime(
            date.year,
            date.month - 1,
            day + 1,
            0,
            0,
            15
          )
        ) {
          daily.open = false;
        }

        return daily;
      });

    this.fillStartCount = calendar.getFillStartCount();
    this.fillEndCount = calendar.daysInCalendar - calendar.getFillEndCount();
  }

  getValidDate(): CalendarEntry {
    let date = this.date;
    if (!date || !("month" in this.date && "year" in this.date)) {
      date = this.today;
    }

    return date;
  }

  dayChangedHandler(calendarEntry: CalendarEntry): void {
    this.dayChanged.emit(calendarEntry);
  }

  daySelectedHandler = (day): void => {
    this.selectedDate = {
      day,
      month: this.date.month,
      year: this.date.year,
    };
    this.dayChangedHandler(this.selectedDate);
  };

  monthChangedHandler(calendarEntry: CalendarEntry): void {
    this.monthChanged.emit(calendarEntry);
  }

  switchToPreviousMonth = (): void => {
    if (this.date.month !== 1) {
      this.date.month -= 1;
    } else {
      this.date.month = 12;
      this.date.year -= 1;
    }

    if (typeof this.date !== "undefined") {
      delete this.date.day;
    }

    this.setCalendarDetails();
    this.monthChangedHandler(this.date);
  };

  switchToNextMonth = (): void => {
    if (this.date.month !== 12) {
      this.date.month += 1;
    } else {
      this.date.month = 1;
      this.date.year += 1;
    }
    if (typeof this.date !== "undefined") {
      delete this.date.day;
    }

    this.setCalendarDetails();
    this.monthChangedHandler(this.date);
  };

  getDigitClassNames = (
    day: number,
    month: number,
    year: number,
    index: number
  ): string => {
    let classNameDigit = [];
    if (day.toString().length === 1) {
      classNameDigit.push("padding-single-digit");
    }

    if (this.isToday(day, month, year, index)) {
      classNameDigit.push("active");
    }

    if (this.isSelectedDay(day, index)) {
      classNameDigit.push("selected");
    }

    if (this.eventDates.includes(day)) {
      classNameDigit.push("has-event");
    }

    return classNameDigit.join(" ");
  };

  isToday(day: number, month: number, year: number, index: number): boolean {
    return (
      this.today.day === day &&
      this.today.month === month &&
      this.today.year === year &&
      this.today.year === year &&
      !(index < this.fillStartCount || index >= this.fillEndCount)
    );
  }

  isSelectedDay(day: number, index: number) {
    return (
      typeof this.selectedDate !== "undefined" &&
      this.selectedDate.day === day &&
      this.selectedDate.month === this.date.month &&
      this.selectedDate.year === this.date.year &&
      !(index < this.fillStartCount || index >= this.fillEndCount)
    );
  }

  isBeforeMinDate(day: number): boolean {
    const date = this.getValidDate();
    if (!this.minDate) {
      return false;
    }
    if (date.year < this.minDate.year) {
      return true;
    } else if (date.year > this.minDate.year) {
      return false;
    } else {
      if (date.month < this.minDate.month) {
        return true;
      } else if (this.minDate.month === date.month) {
        return day < this.minDate.day;
      } else {
        return false;
      }
    }
  }

  private isOpenOn(day: number) {
    return this.dailySchedule[day].open;
  }

  render() {
    let date = this.getValidDate();
    const month = TranslateService.instant(this.monthNames[date.month - 1]);
    return (
      <div class="av-calendar">
        <header>
          <button
            class="av-calendar-button"
            aria-label={TranslateService.instant("Previous month")}
            onClick={this.switchToPreviousMonth}
          >
            &larr;
          </button>
          <span>
            {month} {date.year}
          </span>
          <button
            class="av-calendar-button"
            aria-label={TranslateService.instant("Next month")}
            onClick={this.switchToNextMonth}
          >
            &rarr;
          </button>
        </header>
        <div class="day-names">
          {this.dayNames.map((dayName) => (
            <span>
              <av-translate aria-hidden="true">{dayName}</av-translate>
            </span>
          ))}
        </div>
        <div role="radiogroup" class="days-in-month">
          {this.daysInMonth.map((day, index) => {
            const classNameDigit = this.getDigitClassNames(
              day,
              date.month,
              date.year,
              index
            );
            if (
              index < this.fillStartCount ||
              index >= this.fillEndCount ||
              this.isBeforeMinDate(day) ||
              !this.isOpenOn(day - 1)
            ) {
              return (
                <span role="radio" aria-disabled="true" class="disabled">
                  {this.showFillDays ? day : ""}
                </span>
              );
            } else {
              return (
                <span
                  role="radio"
                  aria-checked={String(classNameDigit.includes("selected"))}
                  onClick={() => this.daySelectedHandler(day)}
                >
                  <i class={classNameDigit}>{day}</i>
                </span>
              );
            }
          })}
        </div>
      </div>
    );
  }
}
